import {
    Box,
    Button,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { HammerIcon } from 'lucide-react';
import { ReactNode, useEffect, useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
    isOverwriteUnpicked,
    overwrittenRoiNumber,
    overwriteTarget,
    NEW_OUTPUT_TARGET,
    OutputTarget,
} from '~/hooks/structure-set/outputTarget';
import { validateStructureName } from '~/utils/validation';
import { STRUCTURE_NAME_MAX_LENGTH } from '~/domain/services/dicomProcessor';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import SectionLabel from '~/components/SectionLabel';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import StructureSelect, { StructureSelectOption } from './StructureSelect';
import { formatColorRgb } from '~/utils/color';
import { PRIMARY_COLOR } from '~/theme/theme';

/** A structure as the operation panels' selects offer it. */
export interface StructureOption {
    name: string;
    roiNumber: number;
    color?: { R: number; G: number; B: number };
    /**
     * True when the ROI cannot receive an operation result: approved, parent
     * set approved, or user-locked. Only ever used to exclude destinations,
     * never rendered as an approval label.
     */
    isWriteProtected: boolean;
}

/** Normalize ROI color to CSS string with fallback */
export const getStructureColor = (color?: {
    R: number;
    G: number;
    B: number;
}): string => (color ? formatColorRgb(color) : PRIMARY_COLOR);

// Helpers

/** Why the operation cannot write to `target`, or undefined when it can. */
export function outputTargetBlocker(
    intl: IntlShape,
    target: OutputTarget
): string | undefined {
    return isOverwriteUnpicked(target)
        ? intl.formatMessage({ id: 'operationPanel.output.pickTarget' })
        : undefined;
}

/**
 * "Create {newName}" or "Overwrite {existing}": the committing action names
 * the structure it writes, so the click is a decision about that structure.
 */
export function outputActionLabel(
    intl: IntlShape,
    target: OutputTarget,
    structures: ReadonlyArray<Pick<StructureOption, 'roiNumber' | 'name'>>,
    newName: string
): string {
    if (target.kind === 'new') {
        return intl.formatMessage(
            { id: 'operationPanel.output.create' },
            { name: newName }
        );
    }
    const name = structures.find((s) => s.roiNumber === target.roiNumber)?.name;
    return name === undefined
        ? intl.formatMessage({ id: 'operationPanel.output.overwriteUnpicked' })
        : intl.formatMessage(
              { id: 'operationPanel.output.overwrite' },
              { name }
          );
}

export const Label = ({
    children,
    required = false,
}: {
    children: React.ReactNode;
    /** Marks a field the operation cannot run without, with the house asterisk. */
    required?: boolean;
}) => (
    <SectionLabel sx={{ mb: 0.75 }}>
        {children}
        {required && (
            <Box component="span" color="error.main" aria-hidden ml={0.25}>
                *
            </Box>
        )}
    </SectionLabel>
);

interface LabeledSelectProps {
    label: string;
    value: number;
    onChange: (roiNumber: number) => void;
    options: readonly StructureSelectOption[];
}

export function LabeledSelect({
    label,
    value,
    onChange,
    options,
}: LabeledSelectProps) {
    return (
        <Box>
            <Label>{label}</Label>
            <StructureSelect
                value={value}
                onChange={onChange}
                options={options}
                aria-label={label}
            />
        </Box>
    );
}

interface ActionButtonsProps {
    onApply: () => void;
    isApplyDisabled?: boolean;
    /** Why the action is unavailable, shown on hover and focus. */
    applyDisabledReason?: string;
    /** Button label; the untranslated default matches the legacy panels. */
    label?: string;
    /** Leading icon, a hammer unless given. Null shows none. */
    startIcon?: React.ReactNode;
    /** Optional test id, stamped on the button root. */
    dataTestId?: string;
}

export function ActionButtons({
    onApply,
    isApplyDisabled,
    applyDisabledReason,
    label = 'Apply',
    startIcon = <HammerIcon size={14} />,
    dataTestId,
}: ActionButtonsProps) {
    const button = (
        <Button
            onClick={onApply}
            variant="contained"
            fullWidth
            size="large"
            disabled={isApplyDisabled}
            startIcon={startIcon}
            data-testid={dataTestId}
        >
            {/* A label naming a long structure ends in an ellipsis rather
                than wrapping, so the button keeps its one row. */}
            <Typography component="span" variant="inherit" noWrap>
                {label}
            </Typography>
        </Button>
    );
    // The reason comes and goes while the form is edited (a preview in
    // progress), so the button must not remount on each flip.
    return (
        <DisabledHint
            title={isApplyDisabled ? applyDisabledReason : undefined}
            spanStyle={{ display: 'flex', width: '100%' }}
            keepWrapper
            placement="bottom"
        >
            {button}
        </DisabledHint>
    );
}

interface OutputStructureSelectProps {
    /** Section label; "Output Structure" unless given. */
    label?: string;
    target: OutputTarget;
    onTargetChange: (target: OutputTarget) => void;
    /** Swatch colour of the operation's input in the overwrite list. */
    inputColor: string;
    inputRoiNumber: number;
    availableStructures: StructureOption[];
    customRoiName: string;
    setCustomRoiName: (v: string) => void;
    customNameError?: string | null;
    /** Placeholder name for the new-structure text field. */
    defaultName: string;
    /** CSS colour of the new structure, shown as a swatch beside its name. */
    newColor?: string;
    /**
     * Offer the "New Structure" mode (the default). Pass false for an
     * operation whose output is restricted to existing structures, such as
     * the threshold-segmentation Body output, where the domain's
     * single-EXTERNAL rule forbids creating a second one. The select then
     * leaves an approved target in place, listed and disabled, so the user
     * can still see which structure the operation is bound to. Blocking
     * the write is the caller's concern, not this control's.
     */
    allowNew?: boolean;
    /**
     * A control set at the end of the mode toggle's row. The toggle then
     * takes its natural width instead of the full row.
     */
    modeAction?: ReactNode;
    /** Content set below the output fields, spaced as the section's rows. */
    children?: ReactNode;
    /** Optional test id, stamped on the select root. */
    dataTestId?: string;
}

export function OutputStructureSelect({
    label: givenLabel,
    target,
    onTargetChange,
    inputColor,
    inputRoiNumber,
    availableStructures,
    customRoiName,
    setCustomRoiName,
    customNameError,
    defaultName,
    allowNew = true,
    newColor,
    modeAction,
    children,
    dataTestId,
}: OutputStructureSelectProps) {
    const intl = useIntl();
    const label =
        givenLabel ?? intl.formatMessage({ id: 'operationPanel.output.label' });
    const inputStructure = availableStructures.find(
        (s) => s.roiNumber === inputRoiNumber
    );

    // Approved structures (ROI- or structure-set-level) cannot receive
    // operation results, so exclude them from the result-target list,
    // except when New Structure is off the menu: there the (single,
    // fixed) target stays listed even approved, rendered disabled below,
    // so a locked operation still shows what it is bound to.
    const otherStructures = useMemo(
        () =>
            availableStructures.filter(
                (s) =>
                    s.roiNumber !== inputRoiNumber &&
                    (!s.isWriteProtected || !allowNew)
            ),
        [availableStructures, inputRoiNumber, allowNew]
    );

    // A picked target that disappears or becomes approved is unpicked, and
    // Apply waits for a new pick.
    const roiNumber = overwrittenRoiNumber(target);
    useEffect(() => {
        if (!allowNew || roiNumber === null) {
            return;
        }
        const picked = availableStructures.find(
            (s) => s.roiNumber === roiNumber
        );
        if (!picked || picked.isWriteProtected) {
            onTargetChange(overwriteTarget(null));
        }
    }, [allowNew, roiNumber, availableStructures, onTargetChange]);

    const canOverwrite =
        (inputStructure !== undefined && !inputStructure.isWriteProtected) ||
        otherStructures.some((s) => !s.isWriteProtected);
    const isNew = allowNew && target.kind === 'new';

    const handleModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        mode: OutputTarget['kind'] | null
    ) => {
        if (mode === 'new') {
            onTargetChange(NEW_OUTPUT_TARGET);
        } else if (mode === 'existing' && canOverwrite) {
            onTargetChange(overwriteTarget(null));
        }
    };

    const targetOptions: StructureSelectOption[] = [
        ...(inputStructure && !inputStructure.isWriteProtected
            ? [
                  {
                      roiNumber: inputRoiNumber,
                      label: `${inputStructure.name} (Current)`,
                      color: inputColor,
                  },
              ]
            : []),
        ...otherStructures.map((s) => ({
            roiNumber: s.roiNumber,
            label: s.name,
            color: getStructureColor(s.color),
            disabled: s.isWriteProtected,
        })),
    ];

    // The mode is also the title of the field under it: a name for a
    // structure to create, or the structure to overwrite.
    const modeToggle = allowNew && (
        <ToggleButtonGroup
            value={target.kind}
            exclusive
            onChange={handleModeChange}
            size="small"
            fullWidth={!modeAction}
            aria-label={label}
        >
            <ToggleButton value="new">
                <FormattedMessage id="operationPanel.output.new" />
            </ToggleButton>
            <ToggleButton value="existing" disabled={!canOverwrite}>
                <FormattedMessage id="operationPanel.output.overwriteExisting" />
            </ToggleButton>
        </ToggleButtonGroup>
    );

    return (
        <Stack gap={1} data-testid={dataTestId}>
            <SectionLabel>{label}</SectionLabel>
            {modeAction ? (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1.5}
                >
                    {modeToggle}
                    {modeAction}
                </Stack>
            ) : (
                modeToggle
            )}
            {isNew ? (
                <TextField
                    value={customRoiName}
                    onChange={(e) => setCustomRoiName(e.target.value)}
                    placeholder={defaultName}
                    size="small"
                    fullWidth
                    inputProps={{
                        maxLength: STRUCTURE_NAME_MAX_LENGTH,
                        'aria-label': 'Name for new structure',
                    }}
                    InputProps={{
                        startAdornment: newColor && (
                            <InputAdornment position="start">
                                <StructureSwatch color={newColor} />
                            </InputAdornment>
                        ),
                    }}
                    error={!!customNameError}
                    helperText={customNameError}
                />
            ) : (
                <StructureSelect
                    value={roiNumber}
                    onChange={(n) => onTargetChange(overwriteTarget(n))}
                    options={targetOptions}
                    aria-label={label}
                    placeholder={intl.formatMessage({
                        id: 'operationPanel.output.pickTarget',
                    })}
                />
            )}
            {allowNew && !canOverwrite && (
                <Typography fontSize={10} color="text.secondary">
                    <FormattedMessage id="operationPanel.output.noOverwriteTarget" />
                </Typography>
            )}
            {children}
        </Stack>
    );
}

/**
 * Validate a custom ROI name against existing structure names.
 * Returns an error string or null if valid.
 * Empty names are allowed (the default name will be used).
 */
export function getCustomNameError(
    customRoiName: string,
    existingNames: string[]
): string | null {
    const trimmed = customRoiName.trim();
    if (trimmed === '') {
        return null; // empty means use default
    }
    const [valid, error] = validateStructureName(trimmed, existingNames);
    return valid ? null : error;
}
